/**
 * A home rail's "See All": GET /v1/rails/{id}, the rail /v1/home showed without its max_items
 * cap, cursor-paged. A page can be short of the limit (or empty) with more to come, so paging
 * follows `page.has_more` only.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, session, unwrap } from '../api';
import { LAYOUT_TO_STYLE, RAIL_LIB, railCardToChannel, VARIANT_TO_STYLE } from '../api/cards';
import ChannelCard from './ChannelCard';
import LoadingSpinner from './common/LoadingSpinner';

const PAGE_SIZE = 40;

// The signed-in viewer's audience, cached per access token: a sign-in, sign-out or token refresh
// asks again. ponytail: a purchase without a token refresh keeps the old audience until the next one.
let audienceCache = { token: null, audience: null };

/** `anon` signed out; signed in, the audience `POST /v1/me/overlay` answers. A failure is `anon`, uncached. */
export async function viewerAudience() {
  const token = session.getAccessToken();
  if (!token) {
    return 'anon';
  }
  if (audienceCache.token === token) {
    return audienceCache.audience;
  }
  try {
    const { data } = await unwrap(
      api.POST('/v1/me/overlay', { params: { query: { platform: 'web' } }, body: { ids: [] } }),
    );
    const audience = data?.audience || 'anon';
    audienceCache = { token, audience };
    return audience;
  } catch (err) {
    return 'anon';
  }
}

export const resetAudienceCache = () => {
  audienceCache = { token: null, audience: null };
};

const RailPage = () => {
  const { id } = useParams();
  const [rail, setRail] = useState(null);
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  // A response for a rail the page has since left is dropped.
  const currentId = useRef(id);
  currentId.current = id;

  const load = useCallback(
    async after => {
      const audience = await viewerAudience();
      const query = {
        platform: 'web',
        lib: RAIL_LIB,
        audience,
        limit: PAGE_SIZE,
        ...(after ? { cursor: after } : {}),
      };
      try {
        const { data } = await unwrap(
          api.GET('/v1/rails/{id}', { params: { path: { id }, query } }),
        );
        if (currentId.current !== id) {
          return;
        }
        // Not live for this platform and audience (or emptied): the new server answers an empty
        // page with no title where develop answered 404.
        if (!after && !data.title && !data.items.length) {
          setError({ code: 'NOT_FOUND' });
          return;
        }
        setRail(data);
        setItems(prev => (after ? [...prev, ...data.items] : data.items));
        setCursor(data.page.has_more ? data.page.next_cursor : null);
      } catch (err) {
        console.error('RailPage: GET /v1/rails failed:', err);
        if (currentId.current !== id) {
          return;
        }
        if (!after) {
          setError(err);
        } else if (err.code === 'BAD_REQUEST') {
          // A stale cursor (the audience changed mid-scroll): start again from page 1.
          load(null);
        }
        // Any other failed later page keeps what is shown and its Load more button.
      }
    },
    [id],
  );

  useEffect(() => {
    setRail(null);
    setItems([]);
    setCursor(null);
    setError(null);
    load(null);
  }, [load]);

  const loadMore = async () => {
    setLoadingMore(true);
    await load(cursor);
    setLoadingMore(false);
  };

  if (error) {
    // 404: unknown, personal or not targeted at web; 400: not a rail id at all.
    const missing = ['NOT_FOUND', 'BAD_REQUEST'].includes(error.code);
    return (
      <div className="min-h-screen bg-primary text-white flex items-center justify-center p-6">
        <div className="text-center">
          <h2 className="mb-2 text-2xl font-display uppercase">
            {missing ? 'Not found' : 'Something went wrong'}
          </h2>
          <p className="mb-6 text-white/70">
            {missing ? "We couldn't find this collection." : "We couldn't load this right now."}
          </p>
          <Link to="/" className="text-accent">
            Back to Home
          </Link>
        </div>
      </div>
    );
  }
  if (!rail) {
    return <LoadingSpinner message="Loading..." />;
  }

  return (
    <div className="min-h-screen bg-primary text-white">
      <div className="container mx-auto px-4 pt-8 pb-16">
        <h1 className="mb-6 font-display text-4xl uppercase tracking-wide">{rail.title?.value}</h1>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {items.map((card, index) => (
            // An edit mid-scroll can repeat an item across pages, so the id alone is no key.
            <ChannelCard
              key={`${card.id}-${index}`}
              channel={railCardToChannel(card)}
              style={rail.variant ? VARIANT_TO_STYLE[rail.variant] : LAYOUT_TO_STYLE[rail.layout]}
              index={index}
            />
          ))}
        </div>
        {cursor && (
          <div className="flex justify-center mt-8">
            <button
              type="button"
              className="px-6 py-3 font-display uppercase tracking-wide border border-white/30 hover:bg-white/10 disabled:opacity-50"
              onClick={loadMore}
              disabled={loadingMore}
            >
              {loadingMore ? 'Loading...' : 'Load more'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RailPage;
